import api from './api';

export interface NotificationTemplate {
  id: number;
  categoria: string;
  evento: string;
  nome: string;
  descricao?: string;
  enable_email: boolean;
  enable_sms: boolean;
  enable_whatsapp: boolean;
  custom_email_message?: string;
  custom_sms_message?: string;
  custom_whatsapp_message?: string;
  custom_email_segundo_envio_msg?: string;
  custom_sms_segundo_envio_msg?: string;
  custom_whatsapp_segundo_envio_msg?: string;
  email_subject?: string;
  ativo: boolean;
  ordem: number;
  primeiro_envio: number;
  segundo_envio: number;
  enviar_fatura_pdf: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationTemplateInput {
  categoria: string;
  evento: string;
  nome?: string;
  descricao?: string;
  enable_email?: boolean;
  enable_sms?: boolean;
  enable_whatsapp?: boolean;
  custom_email_message?: string;
  custom_sms_message?: string;
  custom_whatsapp_message?: string;
  custom_email_segundo_envio_msg?: string;
  custom_sms_segundo_envio_msg?: string;
  custom_whatsapp_segundo_envio_msg?: string;
  email_subject?: string;
  ativo?: boolean;
  ordem?: number;
  primeiro_envio?: number;
  segundo_envio?: number;
  enviar_fatura_pdf?: boolean;
}

export interface NotificationTemplateStats {
  total: number;
  ativos: number;
  inativos: number;
  porCategoria: Array<{
    categoria: string;
    total: number;
  }>;
  canais: {
    email: number;
    sms: number;
    whatsapp: number;
  };
}

class NotificationTemplateService {
  private baseUrl = '/notification-templates';

  // Listar todos os templates
  async getAll(): Promise<NotificationTemplate[]> {
    try {
      const response = await api.get(this.baseUrl);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Erro ao buscar templates:', error);
      throw error;
    }
  }

  // Listar templates agrupados por categoria
  async getByCategory(): Promise<{ [key: string]: NotificationTemplate[] }> {
    try {
      const response = await api.get(`${this.baseUrl}/category`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar templates por categoria:', error);
      throw error;
    }
  }

  // Buscar template por ID
  async getById(id: number): Promise<NotificationTemplate> {
    try {
      const response = await api.get(`${this.baseUrl}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar template por ID:', error);
      throw error;
    }
  }

  // Buscar template por categoria e evento
  async getByEvento(categoria: string, evento: string): Promise<NotificationTemplate | null> {
    try {
      const response = await api.get(`${this.baseUrl}/category/${categoria}/${evento}`);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // Template não encontrado
      }
      console.error('Erro ao buscar template por evento:', error);
      throw error;
    }
  }

  // Criar novo template
  async create(template: NotificationTemplateInput): Promise<NotificationTemplate> {
    try {
      const response = await api.post(this.baseUrl, template);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao criar template:', error);
      throw error;
    }
  }

  // Atualizar template por ID
  async update(id: number, template: Partial<NotificationTemplateInput>): Promise<NotificationTemplate> {
    try {
      const response = await api.put(`${this.baseUrl}/${id}`, template);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      throw error;
    }
  }

  // Atualizar template por categoria/evento (método principal para o frontend)
  async updateByEvento(
    categoria: string, 
    evento: string, 
    template: Partial<NotificationTemplateInput>
  ): Promise<NotificationTemplate> {
    try {
      const response = await api.put(`${this.baseUrl}/category/${categoria}/${evento}`, template);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao atualizar template por evento:', error);
      throw error;
    }
  }

  // Deletar template
  async delete(id: number): Promise<void> {
    try {
      await api.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      throw error;
    }
  }

  // Ativar/Desativar template
  async toggleActive(id: number): Promise<NotificationTemplate> {
    try {
      const response = await api.patch(`${this.baseUrl}/${id}/toggle`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao alternar status do template:', error);
      throw error;
    }
  }

  // Obter estatísticas
  async getStats(): Promise<NotificationTemplateStats> {
    try {
      const response = await api.get(`${this.baseUrl}/stats`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  }

  // Métodos auxiliares para o frontend

  // Salvar template completo (usado no ParametrosTemplates)
  async saveTemplate(
    categoria: string,
    evento: string,
    templateData: {
      enable_email?: boolean;
      enable_sms?: boolean;
      enable_whatsapp?: boolean;
      custom_email_message?: string;
      custom_sms_message?: string;
      custom_whatsapp_message?: string;
      email_subject?: string;
      ativo?: boolean;
      primeiro_envio?: number;
      segundo_envio?: number;
      enviar_fatura_pdf?: boolean;
    }
  ): Promise<NotificationTemplate> {
    try {
      const response = await this.updateByEvento(categoria, evento, templateData);
      return response;
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      throw error;
    }
  }

  // Carregar template para edição (usado no ParametrosTemplates)
  async loadTemplate(categoria: string, evento: string): Promise<NotificationTemplate | null> {
    try {
      const template = await this.getByEvento(categoria, evento);
      return template;
    } catch (error) {
      console.error('Erro ao carregar template:', error);
      throw error;
    }
  }

  // Verificar se template existe
  async exists(categoria: string, evento: string): Promise<boolean> {
    try {
      const template = await this.getByEvento(categoria, evento);
      return template !== null;
    } catch (error) {
      return false;
    }
  }

  // Obter templates por categoria específica
  async getTemplatesByCategory(categoria: string): Promise<NotificationTemplate[]> {
    try {
      const allTemplates = await this.getByCategory();
      return allTemplates[categoria] || [];
    } catch (error) {
      console.error('Erro ao buscar templates da categoria:', error);
      throw error;
    }
  }

  // Validar se template está completo para um canal
  validateChannelContent(template: NotificationTemplate, channel: 'email' | 'sms' | 'whatsapp'): boolean {
    switch (channel) {
      case 'email':
        return template.enable_email && 
               !!template.custom_email_message?.trim() && 
               !!template.email_subject?.trim();
      case 'sms':
        return template.enable_sms && 
               !!template.custom_sms_message?.trim();
      case 'whatsapp':
        return template.enable_whatsapp && 
               !!template.custom_whatsapp_message?.trim();
      default:
        return false;
    }
  }

  // Obter canais habilitados de um template
  getEnabledChannels(template: NotificationTemplate): string[] {
    const channels: string[] = [];
    if (template.enable_email) channels.push('email');
    if (template.enable_sms) channels.push('sms');
    if (template.enable_whatsapp) channels.push('whatsapp');
    return channels;
  }

  // Verificar se template tem pelo menos um canal habilitado
  hasEnabledChannel(template: NotificationTemplate): boolean {
    return template.enable_email || template.enable_sms || template.enable_whatsapp;
  }
}

const notificationTemplateService = new NotificationTemplateService();
export default notificationTemplateService; 